class RhDimensionCalculator extends HTMLElement {
  connectedCallback() {
    this.api = this.dataset.api;
    this.currency = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
    this.price = this.querySelectorAll('[data-rh-price]');
    this.area = this.querySelector('[data-rh-area]');
    this.status = this.querySelector('[data-rh-status]');
    this.file = this.querySelector('[data-rh-file]');
    this.fileStatus = this.querySelector('[data-rh-file-status]');
    this.uploadStep = this.querySelector('[data-rh-upload-step]');
    this.uploadWarning = this.querySelector('[data-rh-upload-warning]');
    this.addButton = this.querySelector('[data-rh-add]');
    this.mobileAddButton = this.querySelector('[data-rh-mobile-add]');
    this.laminate = this.querySelector('[data-rh-laminate]');
    this.laminateFinish = this.querySelector('[data-rh-laminate-finish]');
    this.laminateFinishWrap = this.querySelector('[data-rh-laminate-finish-wrap]');
    this.material = this.querySelector('[data-rh-material]');
    this.materialInfoButton = this.querySelector('[data-rh-material-info-button]');
    this.materialInfo = this.querySelector('[data-rh-material-info]');
    this.dimensionNote = this.querySelector('[data-rh-dimension-note]');
    const productDetails = this.parentElement?.parentElement;
    productDetails?.querySelectorAll('.price, variant-picker, .buy-buttons-block').forEach((element) => { element.hidden = true; });
    this.quote = null;
    this.quoteRequestId = 0;
    this.upload = null;
    this.timer = null;
    this.addEventListener('input', (event) => {
      if (event.target === this.file) return;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.refreshQuote(), 250);
    });
    this.addEventListener('change', (event) => {
      if (event.target === this.file) this.uploadFile();
      else if (event.target === this.laminate) { this.toggleLaminateFinish(); this.refreshQuote(); }
      else this.refreshQuote();
    });
    this.addButton?.addEventListener('click', () => this.addToCart());
    this.mobileAddButton?.addEventListener('click', () => this.addToCart());
    this.materialInfoButton?.addEventListener('click', () => this.toggleMaterialInfo());
    this.material?.addEventListener('change', () => this.updateMaterialInfo());
    this.toggleLaminateFinish();
    this.updateMaterialInfo();
    this.refreshQuote();
  }

  updateMaterialInfo() {
    const details = {
      print: ['Digitaldruckfolie', 'Selbstklebende, bedruckte Folie für glatte Flächen, Schaufenster, Fahrzeuge und bestehende Schilder.'],
      dibond3: ['Alu-Dibond-Platte 3 mm', 'Stabile, wetterfeste Aluminium-Verbundplatte für hochwertige Schilder im Innen- und Außenbereich.'],
      pvc3: ['Hartschaumplatte', 'Leichte und wirtschaftliche Kunststoffplatte für Innenräume, kurzfristige Außenwerbung, Displays und Messeschilder.'],
      acrylic3: ['Plexiglas', 'Hochwertige Acrylglasplatte mit edler Tiefenwirkung – ideal für Praxen, Büros, Empfangsbereiche und Firmenschilder.']
    };
    const selected = details[this.material?.value] || details.dibond3;
    const title = this.querySelector('[data-rh-material-info-title]');
    const text = this.querySelector('[data-rh-material-info-text]');
    if (title) title.textContent = selected[0];
    if (text) text.textContent = selected[1];
    if (this.dimensionNote) this.dimensionNote.textContent = this.material?.value === 'print'
      ? 'Rollenmaterial: maximal 130 cm Druckbreite, die Länge ist frei wählbar.'
      : 'Maximales Plattenformat: 305 × 150 cm, auch gedreht.';
  }

  toggleMaterialInfo() {
    if (!this.materialInfo || !this.materialInfoButton) return;
    const open = this.materialInfo.hidden;
    this.materialInfo.hidden = !open;
    this.materialInfoButton.setAttribute('aria-expanded', String(open));
  }

  setAddDisabled(disabled) {
    if (this.addButton) this.addButton.disabled = disabled;
    if (this.mobileAddButton) this.mobileAddButton.disabled = disabled;
  }

  toggleLaminateFinish() {
    const active = Boolean(this.laminate?.checked);
    this.laminateFinishWrap.hidden = !active;
    this.laminateFinish.disabled = !active;
    if (!active) this.laminateFinish.value = '';
  }

  get configuration() {
    return {
      productId: this.dataset.productId,
      widthCm: Number(this.querySelector('[data-rh-width]').value),
      heightCm: Number(this.querySelector('[data-rh-height]').value),
      material: this.querySelector('[data-rh-material]').value,
      laminate: this.querySelector('[data-rh-laminate]').checked,
      laminateFinish: this.querySelector('[data-rh-laminate-finish]').value,
      contourCut: false,
      holes: Number(this.querySelector('[data-rh-holes]').value),
      quantity: Number(this.querySelector('[data-rh-quantity]').value)
    };
  }

  setStatus(message, error = false) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.dataset.error = error ? 'true' : 'false';
  }

  async request(path, options) {
    const response = await fetch(`${this.api}${path}`, options);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Die Verbindung ist gerade nicht verfügbar.');
    return data;
  }

  async refreshQuote() {
    const requestId = ++this.quoteRequestId;
    if (!this.reportValidity(false)) {
      this.quote = null;
      this.setAddDisabled(true);
      this.price.forEach((node) => { node.textContent = '–'; });
      this.setStatus(this.validationMessage || 'Bitte Eingaben prüfen.');
      return;
    }
    this.setAddDisabled(true);
    this.setStatus('Preis wird berechnet …');
    try {
      const data = await this.request('/api/quote', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(this.configuration)
      });
      if (requestId !== this.quoteRequestId) return;
      this.quote = data;
      this.price.forEach((node) => { node.textContent = this.currency.format(data.totalCents / 100); });
      const config = this.configuration;
      this.area.textContent = `${(config.widthCm * config.heightCm / 10000).toLocaleString('de-DE', { maximumFractionDigits: 3 })} m²`;
      this.setStatus('Onlinepreis inkl. MwSt., zzgl. Versand.');
      this.setAddDisabled(false);
      if (!this.upload) this.setStatus('Druckdatei wird beim Hinzufügen geprüft.');
    } catch (error) {
      if (requestId !== this.quoteRequestId) return;
      this.quote = null;
      this.price.forEach((node) => { node.textContent = '–'; });
      this.setStatus(error.message, true);
    }
  }

  reportValidity(showMessage = true) {
    const width = this.querySelector('[data-rh-width]');
    const height = this.querySelector('[data-rh-height]');
    width.setCustomValidity(''); height.setCustomValidity('');
    const widthCm = Number(width.value), heightCm = Number(height.value);
    const message = this.material?.value === 'print'
      ? (Math.min(widthCm, heightCm) > 130 ? 'Bei Folie muss mindestens eine Seite höchstens 130 cm breit sein.' : '')
      : (!((widthCm <= 305 && heightCm <= 150) || (widthCm <= 150 && heightCm <= 305)) ? 'Platten müssen in das Tafelmaß 305 × 150 cm passen.' : '');
    if (message) { width.setCustomValidity(message); height.setCustomValidity(message); }
    const invalid = [...this.querySelectorAll('input[type="number"], select')].find((field) => !field.checkValidity());
    if (invalid) { this.validationMessage = invalid.validationMessage; if (showMessage) invalid.reportValidity(); return false; }
    this.validationMessage = '';
    return true;
  }

  async uploadFile() {
    const selected = this.file.files?.[0];
    this.upload = null;
    this.clearUploadWarning();
    if (!selected) { this.fileStatus.textContent = `PDF, AI, EPS, SVG, PNG, JPG oder TIFF · max. ${this.dataset.maxFileMb} MB`; return; }
    const maxBytes = Number(this.dataset.maxFileMb) * 1024 * 1024;
    if (!/\.(pdf|ai|eps|svg|png|jpe?g|tiff?)$/i.test(selected.name) || selected.size > maxBytes) {
      this.file.value = '';
      this.fileStatus.textContent = selected.size > maxBytes
        ? `Die Datei ist größer als ${this.dataset.maxFileMb} MB – bitte unten per WeTransfer senden.`
        : `Bitte ein unterstütztes Format bis ${this.dataset.maxFileMb} MB wählen.`;
      this.fileStatus.dataset.error = 'true';
      return;
    }
    this.fileStatus.dataset.error = 'false';
    this.fileStatus.textContent = 'Datei wird sicher hochgeladen …';
    this.setAddDisabled(true);
    try {
      const form = new FormData(); form.append('file', selected);
      this.upload = await this.request('/api/upload', { method: 'POST', body: form });
      this.fileStatus.textContent = `✓ ${this.upload.fileName} hochgeladen`;
    } catch (error) {
      this.fileStatus.textContent = error.message;
      this.fileStatus.dataset.error = 'true';
    } finally {
      this.setAddDisabled(!this.quote);
      if (this.quote && !this.upload && !this.fileStatus.dataset.error) this.setStatus('Druckdatei wird beim Hinzufügen geprüft.');
    }
  }

  clearUploadWarning() {
    if (this.uploadStep) this.uploadStep.dataset.uploadError = 'false';
    if (this.uploadWarning) this.uploadWarning.hidden = true;
  }

  showUploadWarning() {
    if (this.uploadStep) {
      this.uploadStep.dataset.uploadError = 'true';
      this.uploadStep.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    if (this.uploadWarning) this.uploadWarning.hidden = false;
    this.setStatus('Bitte zuerst eine Druckdatei hochladen.', true);
  }

  async addToCart() {
    if (!this.quote || !this.upload || !this.reportValidity()) {
      if (!this.upload) {
        this.showUploadWarning();
      }
      return;
    }
    this.setAddDisabled(true);
    const buttonLabel = this.addButton.querySelector('span');
    if (buttonLabel) buttonLabel.textContent = 'Wird vorbereitet …';
    try {
      const variant = await this.request('/api/cart-variant', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: this.quote.id, signature: this.quote.signature })
      });
      const config = this.configuration;
      const materialLabels = { print: 'Digitaldruckfolie', dibond3: 'Alu-Dibond-Platte 3 mm', pvc3: 'Hartschaumplatte inklusive Druck', acrylic3: 'Plexiglas inklusive Druck' };
      const properties = { 'Maß': `${config.widthCm} × ${config.heightCm} cm`, 'Material': materialLabels[config.material], 'Anzahl': String(config.quantity), 'Schutzfolie (+3 €/m²)': config.laminate ? 'Ja' : 'Nein', 'Bohrungen': String(config.holes), '_Konfigurations-ID': this.quote.id };
      if (config.laminate) properties['Oberfläche Schutzfolie'] = config.laminateFinish === 'matt' ? 'Matt' : 'Glänzend';
      if (this.upload) { properties.Druckdatei = this.upload.fileName; properties['_Upload-ID'] = this.upload.uploadId; }
      let cartResponse;
      for (let attempt = 0; attempt < 4; attempt += 1) {
        if (attempt) await new Promise((resolve) => setTimeout(resolve, attempt * 700));
        cartResponse = await fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: Number(variant.variantId), quantity: 1, properties })
        });
        if (cartResponse.ok || cartResponse.status !== 422) break;
      }
      if (!cartResponse.ok) { const cartError = await cartResponse.json().catch(() => ({})); throw new Error(cartError.description || 'Der Artikel konnte nicht in den Warenkorb gelegt werden.'); }
      window.location.assign('/cart');
    } catch (error) {
      this.setStatus(error.message, true);
      this.setAddDisabled(false);
      if (buttonLabel) buttonLabel.textContent = 'In den Warenkorb';
    }
  }
}
if (!customElements.get('rh-dimension-calculator')) customElements.define('rh-dimension-calculator', RhDimensionCalculator);
